/**
 * In-flight playUri policy (Paul, HEOS zone-hop Oct 21 2026).
 *
 * A Connector play_uri to a player that never fetches the stream runs its whole retry chain
 * (~57 s on HEOS Amps). The TransportCommand mutex queued every later play behind it, so
 * Next / a track pick / a zone switch did nothing for up to a minute, and a re-sent copy of
 * the same play (Play pressed while loading) ran the full failing chain a second time.
 *
 *  - dedupe:  a non-user re-send of the identical play (same player, track, tier) shares the one in flight.
 *  - preempt: a user play (Next / Previous / pick / zone switch) stops waiting for the old one.
 *             Different player: always (the old player is no longer the target). Same player:
 *             only on a Connector that aborts a superseded play_uri chain itself, so the old
 *             chain can never overwrite the new track on the device.
 *  - queue:   everything else keeps the serial mutex order.
 */
import { connectorMeetsMinVersion, getConnectorVersion } from '../utils/connectorVersion';
import { useMaestroStore } from '../store/maestroStore';
import { diagnosticLog } from '../utils/diagnosticLogger';

/** First Connector whose play_uri retry chain aborts when a newer play_uri starts. */
export const MIN_PLAY_SUPERSEDE_CONNECTOR = '11.0.343';

export type InflightPlayUri = {
  rendererId: string;
  trackId: string;
  formatId: number;
  /** Set when a newer user play stopped waiting for this one — its outcome must not touch state. */
  detached: boolean;
  promise?: Promise<void>;
};

export type InflightDecision = 'none' | 'dedupe' | 'preempt' | 'queue';

export function decideInflightPlayUri(
  inflight: InflightPlayUri | null | undefined,
  next: { rendererId: string; trackId: string; formatId: number },
  userInitiated: boolean,
  connectorVersion: string | null | undefined = getConnectorVersion(),
): InflightDecision {
  if (!inflight || inflight.detached) return 'none';
  const identical = inflight.rendererId === next.rendererId
    && inflight.trackId === next.trackId
    && inflight.formatId === next.formatId
    && !!inflight.promise;
  // A re-send of the identical play (Play pressed while loading) shares the one in flight.
  // A user's own re-pick of the same track keeps its own attempt (step-down evidence, #342).
  if (!userInitiated) {
    if (identical) diagnosticLog('info', 'transport', 'play_uri_deduped_inflight', { trackId: next.trackId, formatId: next.formatId });
    return identical ? 'dedupe' : 'queue';
  }
  if (!inflight.rendererId || !next.rendererId) return 'queue'; // local / exclusive engine: keep serial
  if (inflight.rendererId !== next.rendererId) return 'preempt';
  return connectorMeetsMinVersion(connectorVersion, MIN_PLAY_SUPERSEDE_CONNECTOR) ? 'preempt' : 'queue';
}

/** Mark the in-flight play detached and release its caller now; the old IPC finishes in the background. */
export function detachInflightPlayUri(
  inflight: InflightPlayUri | null | undefined,
  reject: ((err: Error) => void) | undefined,
  nextRendererId: string,
): boolean {
  if (!inflight || !reject) return false;
  inflight.detached = true;
  diagnosticLog('info', 'transport', 'play_uri_superseded_inflight', {
    old_renderer_id: inflight.rendererId,
    old_track_id: inflight.trackId,
    new_renderer_id: nextRendererId,
    same_renderer: inflight.rendererId === nextRendererId,
  });
  reject(new Error('Superseded by newer playUri'));
  return true;
}

/** A detached play that later succeeded on a player the user has left is paused there. */
export function settleDetachedPlayUri(
  rendererId: string,
  current: InflightPlayUri | null | undefined,
  pause: (rendererId: string) => Promise<void> | void,
): void {
  const active = useMaestroStore.getState().activeConnectorRenderer?.id;
  if (!rendererId || rendererId === active || current?.rendererId === rendererId) return;
  diagnosticLog('info', 'transport', 'play_uri_detached_cleanup', { renderer_id: rendererId });
  void pause(rendererId);
}

/** The play never reached the device as the current play (queue flush / newer user play) — not a failure. */
export function isPlayUriNotSentError(errMsg: string | null | undefined): boolean {
  const m = String(errMsg || '').toLowerCase();
  return m.includes('flushed by playuri') || m.includes('superseded by newer playuri');
}
